import { z } from 'zod';
import { route, defineContract } from '../core/types';
import { requiredStringSchema, projectIdParamsSchema, coerceBooleanFromString } from '../schemas';
import { projectSchema } from '../../interfaces/IProject';

// ============================================================
// Query/Body Schemas
// ============================================================

const organizationIdQuerySchema = z.object({
	organizationId: requiredStringSchema.describe('Organization identifier'),
});

const demoProjectsQuerySchema = z.object({
	onlyEnabledProjects: coerceBooleanFromString.optional().describe('Filter to only enabled projects'),
});

const createProjectBodySchema = projectSchema.extend({
	_id: z.string().optional(),
	startDate: z.string().optional(),
	endDate: z.string().optional(),
});

/**
 * `projectId` is absent by construction: it is the project's PRIMARY KEY and the
 * URI addresses it, so an update that could set it would rename a project out
 * from under every collection that references it by that string.
 *
 * `expectedDailyWorkforce` is re-declared without its `.default(0)` for the same
 * reason `IConfigFloor` grew a defaults-free base: `.partial()` does not remove
 * a default, so a patch that never mentioned the field would still write 0 over
 * whatever the project had.
 */
const updateProjectBodySchema = createProjectBodySchema
	.omit({ _id: true, projectId: true })
	.extend({ expectedDailyWorkforce: z.number() })
	.partial();

// ============================================================
// Contract
// ============================================================

/**
 * The projects collection itself. `projectId` here is a PRIMARY key rather than
 * a foreign one, which is why the shapes differ from every other file in this
 * service: `/projects/:projectId` is the resource, not a scope prefix.
 *
 * `{ root: true }`, and this file has the most routes that are NOT project data
 * and must therefore spell their old `/project/...` segments literally:
 *
 *  - `getAllProjects`, `getEnabledProjects`, `getDemoProjects` — GLOBAL ADMIN.
 *    Their paths are frozen for a second reason: `Common/src/configs/config.ts`
 *    hardcodes `/api/projectConfig/project/all` and
 *    `/api/projectConfig/project/enabledProjects` across nine environments, and
 *    `requester.service.ts` caches on `^/api/projectConfig/project`.
 *  - `getProjectsByOrganizationId` — ORGANIZATION scope. Its path was a bare
 *    `/`, which under `root` becomes `/api/projectConfig/` — a root catch-all.
 *
 * All three global-admin reads had NO role gate at all before this: any
 * authenticated user of any tenant could enumerate every project in the system.
 */
export const projectContract = defineContract(
	'project',
	{
		/** Global admin: nobody can be a member of a project that does not exist yet. */
		createProject: route({
			method: 'POST',
			path: '/projects',
			body: createProjectBodySchema,
			response: z.string(),
			operationId: 'createProject',
			tags: ['Project'],
			summary: 'Create a project',
		}),
		getAllProjects: route({
			method: 'GET',
			path: '/project/all',
			response: z.array(projectSchema),
			operationId: 'getAllProjects',
			tags: ['Project'],
			summary: 'List every project in the system',
		}),
		getEnabledProjects: route({
			method: 'GET',
			path: '/project/enabledProjects',
			response: z.array(projectSchema),
			operationId: 'getEnabledProjects',
			tags: ['Project'],
			summary: 'List every enabled project in the system',
		}),
		getDemoProjects: route({
			method: 'GET',
			path: '/project/demoProjects',
			query: demoProjectsQuerySchema,
			response: z.array(projectSchema),
			operationId: 'getDemoProjects',
			tags: ['Project'],
			summary: 'List every demo project in the system',
		}),
		/**
		 * Organization scope, and an exact duplicate of
		 * `organization.contract.ts::getProjectsByOrganizationId`. Kept for now —
		 * removing a route is a product decision — but nothing calls this variant.
		 */
		getProjectsByOrganizationId: route({
			method: 'GET',
			path: '/project',
			query: organizationIdQuerySchema,
			response: z.array(projectSchema),
			operationId: 'getProjectsByOrganizationId',
			tags: ['Project'],
			summary: "List an organization's projects",
		}),
		getProjectByProjectId: route({
			method: 'GET',
			path: '/projects/:projectId',
			params: projectIdParamsSchema,
			response: projectSchema,
			operationId: 'getProjectByProjectId',
			tags: ['Project'],
			summary: 'Get a project',
		}),
		updateProject: route({
			method: 'PUT',
			path: '/projects/:projectId',
			params: projectIdParamsSchema,
			body: updateProjectBodySchema,
			response: z.string(),
			operationId: 'updateProject',
			tags: ['Project'],
			summary: 'Update a project',
		}),
		/**
		 * These three expose individual steps of the `createProject` transaction
		 * (`project.service.ts`) as standalone endpoints — three ways to re-run a
		 * lifecycle step against an arbitrary project. None has a caller anywhere.
		 * They are migrated rather than deleted because removing a route is a
		 * product decision, and they are listed as deletion candidates.
		 */
		createProjectInitialProfessions: route({
			method: 'POST',
			path: '/projects/:projectId/professions/initial',
			params: projectIdParamsSchema,
			response: z.string(),
			operationId: 'createProjectInitialProfessions',
			tags: ['Project'],
			summary: 'Re-run the initial-professions step of project creation',
		}),
		addDefaultCertificatesToProject: route({
			method: 'POST',
			path: '/projects/:projectId/certificates/defaults',
			params: projectIdParamsSchema,
			response: z.string(),
			operationId: 'addDefaultCertificatesToProject',
			tags: ['Project'],
			summary: 'Re-run the default-certificates step of project creation',
		}),
		createDefaultUtilitiesForProject: route({
			method: 'POST',
			path: '/projects/:projectId/utilities/defaults',
			params: projectIdParamsSchema,
			response: z.string(),
			operationId: 'createDefaultUtilitiesForProject',
			tags: ['Project'],
			summary: 'Re-run the default-utilities step of project creation',
		}),
	},
	{ root: true }
);
